import { useRef, useEffect, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useSlotGroup,
  useTokens,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { PropertyInfoPanel } from './PropertyInfoPanel';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { getContrastTextColor } from '../utils/luminance';

/** Form card surface — subtitle contrast is computed against this. */
const FORM_CARD_BG = '#1a1a1a';

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a strong full-bleed background photo for a contact form section — interiors, exteriors, or amenity spaces that work behind a dark overlay. Skip logos, floor plans, parking lots, utility areas, and other low-value shots.',
    context:
      'Background behind the Contact Us page lead form. Must not reuse the page hero background image or the Home page Pinned Callouts image.',
  },
};

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  const prefix = 'contactform';
  const formBlockPath = `${prefix}.form_block`;

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-lead-form',
  });

  const bgImage = useSlotGroup('contactform.bg_image', bgImageDef);

  // Black or white against the dark form card — used for subtitle + form copy.
  const formTextColor = getContrastTextColor(FORM_CARD_BG);

  // Match PinnedCallouts CTA hover: secondary fill + contrast label.
  const tokens = useTokens();
  const { ctaHoverBg, ctaHoverText } = useMemo(() => {
    const secondary = tokens?.color?.secondary || '#3E441D';
    return {
      ctaHoverBg: secondary,
      ctaHoverText: getContrastTextColor(secondary),
    };
  }, [tokens]);

  useRevealStyles();
  const contentRef = useScrollReveal<HTMLDivElement>();

  const sectionRef = useRef<HTMLElement>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const parallax = parallaxRef.current;
    if (!section || !parallax) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let rafId = 0;
    let active = false;

    const tick = () => {
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = (vh - rect.top) / (vh + rect.height);
      const clamped = Math.max(0, Math.min(1, progress));
      const offset = (clamped - 0.5) * 80;
      parallax.style.transform = `translateY(${offset}px)`;
      if (active) rafId = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        active = entry.isIntersecting;
        if (active) rafId = requestAnimationFrame(tick);
        else cancelAnimationFrame(rafId);
      },
      { threshold: 0, rootMargin: '200px' },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <>
      {/* ── Section 1: Pinned parallax background + right-aligned lead form ── */}
      <section
        ref={sectionRef}
        className="cf-parallax-section relative overflow-hidden min-h-[480px] md:min-h-[640px] flex items-center justify-center"
      >
        {/* Parallax background */}
        <div
          ref={parallaxRef}
          className="absolute left-0 right-0 z-0"
          style={{ top: '-20%', bottom: '-20%', willChange: 'transform' }}
        >
          <div className="absolute inset-0">
            <EditableSlotGroup groupPath="contactform.bg_image" definition={bgImageDef}>
              {bgImage.image ? (
                <VibesitesImage
                  url={bgImage.image}
                  alt={bgImage.alt || ''}
                  sizes="100vw"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                />
              ) : (
                <div className="absolute inset-0 bg-[var(--vibe-color-text)]" />
              )}
            </EditableSlotGroup>
          </div>
        </div>

        {/* Soft vignette so the photo stays visible around the form */}
        <div className="absolute inset-0 z-[1] pointer-events-none bg-black/25" />

        {/* Right-aligned contact form card (inset from the edge) */}
        <div
          ref={contentRef}
          className="reveal-fade-up relative z-10 w-full px-4 sm:px-6 lg:pl-12 lg:pr-24 xl:pr-32 py-16 md:py-24 flex justify-center lg:justify-end"
        >
          <div
            className="cf-form-card w-full max-w-[560px]"
            style={{
              ['--cf-form-text' as string]: formTextColor,
              ['--cf-form-text-muted' as string]: `color-mix(in srgb, ${formTextColor} 72%, transparent)`,
              ['--cf-form-border' as string]: `color-mix(in srgb, ${formTextColor} 85%, transparent)`,
              ['--cf-cta-hover-bg' as string]: ctaHoverBg,
              ['--cf-cta-hover-text' as string]: ctaHoverText,
            }}
          >
            <RichTextContent
              path="contactform.title"
              label="Title"
              defaultValue="<p>Have Questions?</p>"
              as="h2"
              className="cf-form-title"
            />
            <RichTextContent
              path="contactform.subtitle"
              label="Subtitle"
              defaultValue="<p>Fill out the form below, drop us a line, or stop by to learn how you can start living in Roxanne</p>"
              as="p"
              className="cf-form-subtitle"
            />

            <EditableToken path={formBlockPath} as="div" className="cf-form-block">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <p className="cf-form-empty">No form block selected</p>
              )}
            </EditableToken>
          </div>
        </div>
      </section>

      {/* ── Section 2: Contact info in a horizontal row ── */}
      <div className="cf-info-row-section">
        <div className="cf-info-row-container">
          <div className="cf-info-row-layout">
            <PropertyInfoPanel prefix={prefix} showVisit={false} showCall={false} />
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — static paths so the linter can match each schema key.
          The live EditableTokens in PropertyInfoPanel use dynamic paths from prefix. */}
      <EditableToken path="contactform.form_block"   as="div"  className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.hours_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        /* ── Form card (mirrors PinnedCallouts) ── */
        .cf-form-card {
          background: #1a1a1a;
          padding: 2rem 1.75rem 2.25rem;
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.35);
        }
        @media (min-width: 768px) {
          .cf-form-card {
            padding: 2.75rem 3rem 3rem;
          }
        }

        .cf-form-title {
          font-family: Georgia, 'Times New Roman', serif;
          font-size: 1.75rem;
          font-weight: 400;
          line-height: 1.2;
          color: var(--cf-form-text, #ffffff);
          text-align: left;
          margin: 0 0 0.75rem;
        }
        @media (min-width: 768px) {
          .cf-form-title {
            font-size: 2.125rem;
          }
        }
        .cf-form-title p {
          margin: 0;
        }

        .cf-form-subtitle {
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          line-height: 1.55;
          color: var(--cf-form-text, #ffffff);
          text-align: left;
          margin: 0 0 1.75rem;
        }
        .cf-form-subtitle p {
          margin: 0;
        }

        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--cf-form-text-muted, rgba(255, 255, 255, 0.72));
          text-align: center;
          margin: 0;
          padding: 1.5rem 0;
        }

        /* Hide the lead-form's own header — we render title/subtitle above */
        .cf-form-block .mh-lead-form-card > h3,
        .cf-form-block .mh-lead-form-card > p {
          display: none !important;
        }

        /* Transparent card shell — outer .cf-form-card provides the dark panel */
        .cf-form-block .mh-lead-form-card {
          background: transparent !important;
          background-color: transparent !important;
          box-shadow: none !important;
          border-radius: 0 !important;
          padding: 0 !important;
          max-width: none !important;
        }

        .cf-form-block .mh-lead-form-card form {
          display: grid !important;
          grid-template-columns: 1fr 1fr;
          gap: 1.35rem 1.5rem !important;
        }

        .cf-form-block .mh-lead-form-card form > * {
          grid-column: 1 / -1;
          min-width: 0;
        }

        /* Email + phone sit side-by-side on desktop */
        .cf-form-block .mh-lead-form-card form > .mh-lead-form-name-row + div {
          grid-column: 1;
        }
        .cf-form-block .mh-lead-form-card form > .mh-lead-form-name-row + div + div {
          grid-column: 2;
        }

        @media (max-width: 639px) {
          .cf-form-block .mh-lead-form-card form {
            grid-template-columns: 1fr;
          }
          .cf-form-block .mh-lead-form-card form > .mh-lead-form-name-row + div,
          .cf-form-block .mh-lead-form-card form > .mh-lead-form-name-row + div + div {
            grid-column: 1;
          }
          .cf-form-block .mh-lead-form-name-row,
          .cf-form-block .mh-lead-form-field-row {
            grid-template-columns: 1fr !important;
          }
        }

        /* Underline field style — contrast text on the dark card */
        .cf-form-block .mh-lead-form-card input:not([type="checkbox"]):not([type="date"]),
        .cf-form-block .mh-lead-form-card select,
        .cf-form-block .mh-lead-form-card textarea {
          background: transparent !important;
          background-color: transparent !important;
          border: none !important;
          border-bottom: 1px solid var(--cf-form-border, rgba(255, 255, 255, 0.85)) !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          color: var(--cf-form-text, #ffffff) !important;
          -webkit-text-fill-color: var(--cf-form-text, #ffffff) !important;
          padding: 0.35rem 0 0.55rem !important;
          height: auto !important;
          min-height: 2.5rem;
          font-size: 0.9375rem !important;
        }

        .cf-form-block .mh-lead-form-card input:not([type="checkbox"]):not([type="date"])::placeholder,
        .cf-form-block .mh-lead-form-card textarea::placeholder {
          color: var(--cf-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
          -webkit-text-fill-color: var(--cf-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
          opacity: 1;
          font-size: 0.8125rem;
        }

        .cf-form-block .mh-lead-form-card select {
          padding-right: 1.75rem !important;
        }
        .cf-form-block .mh-lead-form-card select option {
          color: #111;
          background: #fff;
          -webkit-text-fill-color: #111;
        }

        .cf-form-block .mh-lead-form-card textarea {
          min-height: 4.5rem;
          resize: vertical;
        }

        .cf-form-block .mh-lead-form-card input:focus,
        .cf-form-block .mh-lead-form-card select:focus,
        .cf-form-block .mh-lead-form-card textarea:focus {
          outline: none !important;
          border-bottom-color: var(--cf-form-text, #ffffff) !important;
        }

        /* Date field: match full-width underline of sibling fields */
        .cf-form-block .mh-lead-form-date-field {
          background: color-mix(in srgb, var(--cf-form-text, #ffffff) 6%, transparent);
          padding: 0;
          border-bottom: 1px solid var(--cf-form-border, rgba(255, 255, 255, 0.85));
        }
        .cf-form-block .mh-lead-form-date-field input[type="date"] {
          background: transparent !important;
          background-color: transparent !important;
          border: none !important;
          border-bottom: none !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          padding: 0.35rem 2rem 0.55rem 0.65rem !important;
          height: auto !important;
          min-height: 2.5rem;
          font-size: 0.9375rem !important;
          /* Empty (required + invalid): hide native date ghost text so the
             custom "Desired Move-in Date" label is the only visible copy */
          color: transparent !important;
          -webkit-text-fill-color: transparent !important;
        }
        .cf-form-block .mh-lead-form-date-field input[type="date"]:focus {
          outline: none !important;
        }
        .cf-form-block .mh-lead-form-date-field:focus-within {
          border-bottom-color: var(--cf-form-text, #ffffff);
        }
        .cf-form-block .mh-lead-form-date-field input[type="date"]:valid {
          color: var(--cf-form-text, #ffffff) !important;
          -webkit-text-fill-color: var(--cf-form-text, #ffffff) !important;
        }
        .cf-form-block .mh-lead-form-date-field input[type="date"]::-webkit-datetime-edit,
        .cf-form-block .mh-lead-form-date-field input[type="date"]::-webkit-datetime-edit-fields-wrapper,
        .cf-form-block .mh-lead-form-date-field input[type="date"]::-webkit-datetime-edit-text,
        .cf-form-block .mh-lead-form-date-field input[type="date"]::-webkit-datetime-edit-month-field,
        .cf-form-block .mh-lead-form-date-field input[type="date"]::-webkit-datetime-edit-day-field,
        .cf-form-block .mh-lead-form-date-field input[type="date"]::-webkit-datetime-edit-year-field {
          color: transparent;
          -webkit-text-fill-color: transparent;
          padding: 0;
        }
        .cf-form-block .mh-lead-form-date-field input[type="date"]:valid::-webkit-datetime-edit,
        .cf-form-block .mh-lead-form-date-field input[type="date"]:valid::-webkit-datetime-edit-fields-wrapper,
        .cf-form-block .mh-lead-form-date-field input[type="date"]:valid::-webkit-datetime-edit-text,
        .cf-form-block .mh-lead-form-date-field input[type="date"]:valid::-webkit-datetime-edit-month-field,
        .cf-form-block .mh-lead-form-date-field input[type="date"]:valid::-webkit-datetime-edit-day-field,
        .cf-form-block .mh-lead-form-date-field input[type="date"]:valid::-webkit-datetime-edit-year-field {
          color: var(--cf-form-text, #ffffff);
          -webkit-text-fill-color: var(--cf-form-text, #ffffff);
        }
        .cf-form-block .mh-lead-form-date-field > span {
          left: 0.65rem !important;
          color: var(--cf-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
          font-size: 0.8125rem !important;
        }

        .cf-form-block .mh-lead-form-card svg {
          color: var(--cf-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
          stroke: var(--cf-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
          right: 0.35rem !important;
        }

        /* Override block inline grays / dark text that fail on the dark card */
        .cf-form-block .mh-lead-form-card [style*="color: rgb(107, 114, 128)"],
        .cf-form-block .mh-lead-form-card [style*="color: #6b7280"],
        .cf-form-block .mh-lead-form-card [style*="color: #9ca3af"],
        .cf-form-block .mh-lead-form-card [style*="color: #374151"],
        .cf-form-block .mh-lead-form-card [style*="color: #111827"],
        .cf-form-block .mh-lead-form-card label,
        .cf-form-block .mh-lead-form-card label span,
        .cf-form-block .mh-lead-form-card p:not([role="alert"]) {
          color: var(--cf-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
        }

        /* Submit — outline in contrast text, left-aligned */
        .cf-form-block .mh-lead-form-submit-button {
          width: auto !important;
          max-width: none !important;
          align-self: start;
          justify-self: start;
          margin-top: 0.35rem;
          background: transparent !important;
          background-color: transparent !important;
          color: var(--cf-form-text, #ffffff) !important;
          border: 1px solid var(--cf-form-text, #ffffff) !important;
          border-radius: 0 !important;
          padding: 0.7rem 1.75rem !important;
          font-size: 0.8125rem !important;
          font-weight: 500 !important;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          transition: background-color 0.25s ease, color 0.25s ease;
        }
        .cf-form-block .mh-lead-form-submit-button:hover:not(:disabled) {
          background: var(--cf-cta-hover-bg) !important;
          border-color: var(--cf-cta-hover-bg) !important;
          color: var(--cf-cta-hover-text) !important;
        }

        .cf-form-block .mh-lead-form-card a {
          color: var(--cf-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
        }

        /* Success state on dark panel */
        .cf-form-block .mh-lead-form-card [role="status"] h3 {
          color: var(--cf-form-text, #ffffff) !important;
        }
        .cf-form-block .mh-lead-form-card [role="status"] p {
          color: var(--cf-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
        }
        .cf-form-block .mh-lead-form-success-cta {
          background: transparent !important;
          border: 1px solid var(--cf-form-text, #ffffff) !important;
          color: var(--cf-form-text, #ffffff) !important;
          border-radius: 0 !important;
        }

        @media (prefers-reduced-motion: reduce) {
          .cf-parallax-section [style*="will-change"] {
            transform: none !important;
          }
        }

        /* ── Contact info row section ── */
        .cf-info-row-section {
          display: none;
          background: white;
          padding: 2rem 0;
          border-top: 1px solid var(--vibe-color-border, #e5e7eb);
        }
        @media (min-width: 768px) {
          .cf-info-row-section:has(.cf-card) { display: block; }
        }
        .cf-info-row-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        @media (min-width: 1024px) {
          .cf-info-row-container { padding: 0 2rem; }
        }

        /* Override info-cards to flow horizontally */
        .cf-info-row-layout .cf-info-cards {
          flex-direction: row;
          flex-wrap: wrap;
          background: transparent;
          border: none;
          border-radius: 0;
          overflow: visible;
          gap: 0.75rem;
        }
        /* Force the Office Hours label to render in plain black instead of
           the primary brand color, scoped to this page so the shared
           PropertyInfoPanel keeps its default styling elsewhere
           (e.g. ScheduleTourForm). */
        .cf-info-row-layout .cf-card-label {
          color: #000;
        }
        .cf-info-row-layout .cf-card {
          flex: 1 1 180px;
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
          border-radius: var(--vibe-radius-lg, 12px);
          background: white;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
          padding: 0.75rem;
        }
        .cf-info-row-layout .cf-card:last-child {
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
        }

        /* Tablet: tighten the row container and card padding to match shrunken content */
        @media (min-width: 768px) and (max-width: 1023px) {
          .cf-info-row-section {
            padding: 1.25rem 0;
          }
          .cf-info-row-layout .cf-info-cards {
            gap: 0.5rem;
          }
          .cf-info-row-layout .cf-card {
            flex: 1 1 140px;
            padding: 0.5rem;
          }
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Have Questions?</p>',
    },
    'contactform.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Fill out the form below, drop us a line, or stop by to learn how you can start living in Roxanne</p>',
    },
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactform.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'contactform.bg_image': bgImageDef,
  },
  collectionBindings: {
    'contactform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
